import { FC, memo, type ReactNode } from 'react';
import cn from 'classnames';
import { Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { TaskResult } from '../../../../models/enums/TaskResult';
import {
  journalTaskStatusLabel,
  TaskProcessingStatus,
} from '../../../../models/enums/TaskProcessingStatus';
import { AttemptStateOptions } from '../../../../models/enums/AttemptState';
import { Clinic } from '../../../../models/MedVox/Clinic';
import {
  Specialization,
  SurnameDoctor,
} from '../../../../models/OutgoingCalls/Doctors';
import { OptionLike } from '../../../../hooks/shared/useFilterOption';
import { useFilterOptions } from '../../../../hooks/shared/useFilterOptions';
import { JournalDictionaryOption } from '../../../../hooks/shared/useJournalQueryState';
import { FILTER_FIELD_TEXTS } from '../../../../components/shared/filterFieldTexts';
import { ClinicSelect } from '../../../../components/shared/ClinicSelect';
import filterDefaults from '../../../../components/shared/filterPanelDefaults.module.css';
import { AttemptTimeRangeFilter } from './AttemptTimeRangeFilter';

interface Props {
  campaignFilter?: ReactNode;
  clinics: Clinic[];
  specializations: Specialization[];
  surnameDoctors: SurnameDoctor[];
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  filterOption: (
    inputValue: string,
    option?: OptionLike | undefined,
  ) => boolean;

  onClinicsChange: (value: string[]) => void;
  clinicValue: string[];
  resultValue: string[];
  onResultChange: (value: string[]) => void;
  taskStatusValue: string[];
  onTaskStatusChange: (value: string[]) => void;
  attemptStatusValue: string[];
  onAttemptStatusChange: (value: string[]) => void;
  specializationValue: string[];
  onSpecializationChange: (value: string[]) => void;
  doctorsValue: string[];
  onDoctorsChange: (value: string[]) => void;
  onOpenClinics: (value: boolean) => void;
  onOpenResult: (value: boolean) => void;
  onOpenTaskStatus: (value: boolean) => void;
  onOpenAttemptStatus: (value: boolean) => void;
  onOpenSpecialization: (value: boolean) => void;
  onOpenDoctors: (value: boolean) => void;
  notificationTypeValue: string[];
  onNotificationTypeChange: (value: string[]) => void;
  onOpenNotificationType: (value: boolean) => void;
  taskStatusOptions?: JournalDictionaryOption[];
  resultOptions?: JournalDictionaryOption[];
  scenarioOptions?: JournalDictionaryOption[];
  attemptTimeRangeValue: [Dayjs, Dayjs] | null;
  onAttemptTimeRangeChange: (value: [Dayjs, Dayjs] | null) => void;
  disabled?: boolean;
}

export const OutgoingCallsFilter: FC<Props> = memo(
  ({
    campaignFilter,
    clinics,
    specializations,
    surnameDoctors,
    filterOption,
    onClinicsChange,
    clinicValue,
    resultValue,
    onResultChange,
    taskStatusValue,
    onTaskStatusChange,
    attemptStatusValue,
    onAttemptStatusChange,
    specializationValue,
    onSpecializationChange,
    doctorsValue,
    onDoctorsChange,
    onOpenClinics,
    onOpenResult,
    onOpenTaskStatus,
    onOpenAttemptStatus,
    onOpenSpecialization,
    onOpenDoctors,
    notificationTypeValue,
    onNotificationTypeChange,
    onOpenNotificationType,
    taskStatusOptions = [],
    resultOptions = [],
    scenarioOptions = [],
    attemptTimeRangeValue,
    onAttemptTimeRangeChange,
    disabled = false,
  }: Props) => {
    const scenarioSource = scenarioOptions;
    const resultSource =
      resultOptions.length > 0
        ? resultOptions
        : TaskResult.map((result) => ({
            value: result.value,
            id: result.value,
            label: result.text,
          }));
    const taskStatusSource =
      taskStatusOptions.length > 0
        ? taskStatusOptions.map((status) => ({
            ...status,
            label: journalTaskStatusLabel(status.value),
          }))
        : TaskProcessingStatus.map((status) => ({
            value: status.value,
            id: status.value,
            label: journalTaskStatusLabel(status.value),
          }));

    const notificationTypesArray = useFilterOptions(
      scenarioSource,
      notificationTypeValue,
      (t) => notificationTypeValue.includes(t.value),
      (t) => t,
    );

    const taskResultArray = useFilterOptions(
      resultSource,
      resultValue,
      (s) => resultValue.includes(s.value),
      (s) => s,
    );

    const taskStatusArray = useFilterOptions(
      taskStatusSource,
      taskStatusValue,
      (s) => taskStatusValue.includes(s.value),
      (s) => s,
    );

    const attemptStatusArray = useFilterOptions(
      AttemptStateOptions,
      attemptStatusValue,
      (s) => attemptStatusValue.includes(s.value),
      (s) => ({ value: s.value, id: s.value, label: s.text }),
    );

    const specializationsArray = useFilterOptions(
      specializations,
      specializationValue,
      (s) => specializationValue.includes(s.id),
      (s) => ({ value: s.id, id: s.id, label: s.title }),
    );

    const surnamesArray = useFilterOptions(
      surnameDoctors,
      doctorsValue,
      (s) => doctorsValue.includes(s.id),
      (s) => ({
        value: s.id,
        id: s.id,
        label: `${s.surname} ${s.givenName} ${s.patronym}`,
      }),
    );

    return (
      <>
        <div className={filterDefaults.filterBlockColumn}>
          <AttemptTimeRangeFilter
            value={attemptTimeRangeValue}
            onChange={onAttemptTimeRangeChange}
            disabled={disabled}
          />
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.taskStatus.label}
            </label>
            <Select
              allowClear
              className={cn(filterDefaults.filterInput)}
              placeholder={FILTER_FIELD_TEXTS.taskStatus.placeholder}
              mode="multiple"
              value={taskStatusValue}
              filterOption={filterOption}
              onChange={onTaskStatusChange}
              showSearch={true}
              onOpenChange={onOpenTaskStatus}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={taskStatusArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.notificationType.label}
            </label>
            <Select
              allowClear
              className={cn(filterDefaults.filterInput)}
              placeholder={FILTER_FIELD_TEXTS.notificationType.placeholder}
              mode="multiple"
              value={notificationTypeValue}
              filterOption={filterOption}
              onChange={onNotificationTypeChange}
              showSearch={true}
              onOpenChange={onOpenNotificationType}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={notificationTypesArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
          {campaignFilter}
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.clinic.label}
            </label>
            <ClinicSelect
              clinics={clinics}
              value={clinicValue}
              onChange={onClinicsChange}
              className={cn(filterDefaults.filterInput)}
              placeholder={FILTER_FIELD_TEXTS.clinic.placeholder}
              filterOption={filterOption}
              onOpenChange={onOpenClinics}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            />
          </div>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.taskResult.label}
            </label>
            <Select
              allowClear
              className={cn(filterDefaults.filterInput)}
              placeholder={FILTER_FIELD_TEXTS.taskResult.placeholder}
              mode="multiple"
              value={resultValue}
              filterOption={filterOption}
              onChange={onResultChange}
              showSearch={true}
              onOpenChange={onOpenResult}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={taskResultArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.attemptStatus.label}
            </label>
            <Select
              allowClear
              className={cn(filterDefaults.filterInput)}
              placeholder={FILTER_FIELD_TEXTS.attemptStatus.placeholder}
              mode="multiple"
              value={attemptStatusValue}
              filterOption={filterOption}
              onChange={onAttemptStatusChange}
              showSearch={true}
              onOpenChange={onOpenAttemptStatus}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={attemptStatusArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
        </div>
      </>
    );
  },
);

OutgoingCallsFilter.displayName = 'OutgoingCallsFilter';
